// /filings - matters ready to leave the office, by the same definition the
// rail badge counts (server /api/firm/counts readyToFile): open, filing
// stage, nothing missing. Every ingest path today leaves caseStage at
// "intake", so an empty queue is the expected state until staff move a
// matter's stage - the page stays honest rather than widening the filter.
// Open and filing-stage are the server's ask (?status=open&caseStage=filing);
// the list API cannot ask for an empty missingInfo, so that check runs here.

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { practiceAreaById } from "../../data/practice-areas";
import { Skeleton, SkeletonGroup } from "../../components/chrome/Skeleton";
import { EmptyCopy } from "../../components/chrome/EmptyCopy";
import { SepList, SEP } from "../../components/chrome/SepList";
import { useFilingsQueue } from "../../hooks/useFilingsQueue";
import type { Matter } from "../../data/types";
import { fmtDays } from "../../lib/dates";
import { isPastEvent } from "../../api/firmDeadlines";
import { clientNameLabel, matterKindLabel } from "../../lib/caseLabels";

// readyToFile's last clause - the one the list API cannot ask for.
const nothingMissing = (m: Matter) => (m.missing?.length ?? 0) === 0;

// Past the missing-info filter, needsAttention can only mean priority
// "urgent".
function urgency(m: Matter): number {
  const ds = m.deadlines || [];
  if (ds.some((d) => d.bucket === "critical")) return 3;
  if (ds.some((d) => d.bucket === "thisWeek")) return 2;
  if (m.needsAttention) return 1;
  return 0;
}

function rowSub(m: Matter): ReactNode[] {
  const parts: ReactNode[] = [matterKindLabel(m)];
  if (m.matter?.venue) parts.push(m.matter.venue);
  if (m.needsAttention) parts.push("needs attention");
  return parts;
}

export function FilingsIndex() {
  const { data, isLoading, isError } = useFilingsQueue();
  const page = data?.matters ?? [];
  const ranked = page.filter(nothingMissing).sort((a, b) => urgency(b) - urgency(a));
  const capped = data ? data.total > page.length : false;
  const noun = ranked.length === 1 ? "matter" : "matters";

  if (isLoading) {
    return (
      <main className="cc-main">
        <div className="cc-body">
          <SkeletonGroup label="Loading filings">
            <Skeleton width="30%" height={22} />
            <Skeleton width="100%" height={84} radius={10} />
            <Skeleton width="100%" height={84} radius={10} />
          </SkeletonGroup>
        </div>
      </main>
    );
  }

  return (
    <main className="cc-main">
      <div className="cc-body">
        <div className="cc-index-head">
          <span className="title">Filings</span>
          {data && (
            <span className="count">
              {ranked.length} {noun} ready to file
            </span>
          )}
          <span className="blurb">
            Open matters in the filing stage with nothing missing - the same definition the rail
            badge counts. Sorted by deadline urgency.
            {capped ? ` Checked against the newest ${page.length} of ${data?.total}.` : ""}
          </span>
        </div>

        {isError && (
          <div
            className="cc-card"
            style={{ padding: "10px 18px", marginBottom: 12, color: "var(--warn-600)" }}
          >
            Could not reach the server - the filings queue is unavailable.
          </div>
        )}

        {data && (
          <div className="cc-card">
            {ranked.length === 0 ? (
              <div className="cc-empty">
                <EmptyCopy
                  title="No filings queued"
                  mine="No filings queued on the matters assigned to you"
                  body="A matter appears here once it is open, its case stage is set to filing, and no required information is missing."
                />
              </div>
            ) : (
              ranked.map((m) => {
                const pa = practiceAreaById(m.practiceArea);
                // A hearing that went by is not a filing's note.
                const rows = (m.deadlines || []).filter((d) => !isPastEvent(d));
                const crit = rows.find((d) => d.bucket === "critical" || d.bucket === "passed");
                const sub = rowSub(m);
                return (
                  <Link key={m.eID} to={`/matter/${m.eID}`} className="cc-index-row cc-row-lift">
                    <span
                      className="pa-dot lg"
                      style={{ background: pa?.accent ?? "var(--muted)" }}
                    />
                    <div>
                      <div className="row-title">{clientNameLabel(m)}</div>
                      <div className="row-sub">
                        <SepList parts={sub} />
                      </div>
                    </div>
                    <div className="row-meta">
                      <span className="tnum-mono">{m.eID}</span>
                      <div
                        className="row-meta-sub"
                        style={crit ? { color: "var(--red-600)", fontWeight: 600 } : undefined}
                      >
                        {crit
                          ? `${crit.bucket === "passed" ? "Overdue" : "Critical"}${SEP}${fmtDays(crit.days)}`
                          : (rows[0]?.label ?? "")}
                      </div>
                    </div>
                  </Link>
                );
              })
            )}
          </div>
        )}

        {data && (
          <div className="cc-detail-footer">
            <span>
              {ranked.length} {noun} in queue
            </span>
            <span className="sep">{SEP.trim()}</span>
            <span>Set an open matter's case stage to filing to queue it</span>
          </div>
        )}
      </div>
    </main>
  );
}
